import { useState, type ReactNode } from 'react';
import { Modal } from '../ui';

/** A confirmation that arms only when `word` is typed back exactly. `run` resolves when the action is done; a thrown
 *  error is shown inline and the dialog stays open. */
export default function TypedConfirm({
  title, word, wordLabel, confirmLabel, children, onClose, run,
}: {
  title: ReactNode;
  word: string;
  wordLabel?: ReactNode;
  confirmLabel: string;
  children?: ReactNode;
  onClose: () => void;
  run: () => Promise<void>;
}) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const armed = typed.trim() === word && !busy;
  const go = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await run();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };
  return (
    <Modal
      title={title}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn primary" onClick={() => void go()} disabled={!armed}>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </>
      }
    >
      {children}
      <label>
        {wordLabel ?? <>Type <code>{word}</code> to confirm</>}
        <br />
        <input
          type="text"
          aria-label="typed confirmation"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={word}
          style={{ width: '100%', fontFamily: 'monospace' }}
          autoFocus
        />
      </label>
      {err ? (
        <div className="notice" style={{ marginTop: 10 }}>
          <code>{err}</code>
        </div>
      ) : null}
    </Modal>
  );
}
